import {
  useGetDefaultConfigs,
  useGetSupportedCurrencies,
  useGetSupportedPartners,
  useGetTemplates,
  useRemoveDefaultConfig,
  useUpdateDefaultConfig,
} from '@domains/subscription/priv/api/queries'
import { DefaultConfigDetails } from '@domains/subscription/priv/DefaultConfigDetails/DefaultConfigDetails'
import type { CreateDefaultConfigFormData } from '@domains/subscription/priv/Templates/types'
import useMaterialNotification from 'hooks/useMaterialNotification'

export interface DefaultConfigDetailsNavigation {
  back: () => void
}

interface DefaultConfigDetailsFactoryProps {
  configId: string
  navigation: DefaultConfigDetailsNavigation
}

export const DefaultConfigDetailsFactory = ({ configId, navigation }: DefaultConfigDetailsFactoryProps) => {
  const notify = useMaterialNotification()
  const { data: defaultConfigs = [] } = useGetDefaultConfigs()
  const { data: templates = [] } = useGetTemplates()
  const { data: supportedCurrencies = [] } = useGetSupportedCurrencies()
  const { data: supportedPartners = [] } = useGetSupportedPartners()
  const updateDefaultConfig = useUpdateDefaultConfig()
  const removeDefaultConfig = useRemoveDefaultConfig()

  const config = defaultConfigs.find((c) => c.id === configId)

  const handleUpdate = (data: CreateDefaultConfigFormData) => {
    updateDefaultConfig.mutate(
      { configId, data },
      {
        onSuccess: () => {
          notify('Default config updated successfully')
        },
      },
    )
  }

  const handleDelete = () => {
    removeDefaultConfig.mutate(configId, {
      onSuccess: () => {
        notify('Default config deleted successfully')
        navigation.back()
      },
    })
  }

  if (!config) {
    return null
  }

  return (
    <DefaultConfigDetails
      config={config}
      templates={templates.map((t) => ({ id: t.id, name: t.name }))}
      supportedCurrencies={supportedCurrencies}
      supportedPartners={supportedPartners}
      navigateBack={navigation.back}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      isUpdating={updateDefaultConfig.isLoading}
    />
  )
}
